// Notebook Design System - Color Palette & Theme Constants

export const notebookPalette = {
  // Paper colors
  paper: {
    cream: '#fdfcf7',
    ivory: '#fffef9',
    aged: '#f5f0e1',
    graph: '#f8fbfc',
    lined: '#fdfaf5',
  },

  // Pencil/ink colors (graphite grades)
  pencil: {
    light: '#9ca3af',    // HB light
    medium: '#6b7280',   // 2B medium
    dark: '#374151',     // 4B dark
    graphite: '#1f2937', // 6B graphite
    charcoal: '#111827', // 8B charcoal
  },

  // Colored pencil accents
  coloredPencil: {
    red: '#dc2626',
    orange: '#ea580c',
    yellow: '#ca8a04',
    green: '#16a34a',
    blue: '#2563eb',
    purple: '#7c3aed',
    pink: '#db2777',
    teal: '#0d9488',
  },

  // Highlighter effects (semi-transparent)
  highlighter: {
    yellow: 'rgba(253, 224, 71, 0.4)',
    green: 'rgba(134, 239, 172, 0.4)',
    pink: 'rgba(249, 168, 212, 0.4)',
    blue: 'rgba(147, 197, 253, 0.4)',
    orange: 'rgba(253, 186, 116, 0.4)',
  },

  // Paper elements
  elements: {
    ruledLine: '#d1e0ed',
    marginLine: '#f8b4b4',
    gridLine: '#e5e7eb',
    dotColor: '#d1d5db',
    binderHole: '#e5e7eb',
  },

  // Notebook cover colors
  cover: {
    leather: '#5c4033',
    burgundy: '#722f37',
    navy: '#1e3a5a',
    forest: '#2d4a3e',
    black: '#1a1a1a',
  },

  // Personality elements
  personality: {
    coffeeStain: 'rgba(139, 90, 43, 0.15)',
    tapeYellow: 'rgba(253, 224, 71, 0.6)',
    tapePink: 'rgba(249, 168, 212, 0.6)',
    tapeBlue: 'rgba(147, 197, 253, 0.6)',
    tapeGreen: 'rgba(134, 239, 172, 0.6)',
    stickyYellow: '#fef08a',
    stickyPink: '#fbcfe8',
    stickyGreen: '#bbf7d0',
    stickyBlue: '#bfdbfe',
  },
} as const;

// Animation timing constants
export const notebookAnimations = {
  draw: {
    duration: 1.5,
    ease: [0.4, 0, 0.2, 1],
  },
  pageFlip: {
    duration: 0.6,
    ease: [0.4, 0, 0.2, 1],
  },
  fadeIn: {
    duration: 0.4,
    ease: [0.4, 0, 0.2, 1],
  },
  wobble: {
    duration: 0.3,
    ease: 'easeInOut',
  },
  float: {
    duration: 6,
    ease: 'easeInOut',
  },
} as const;

// Wobble factors for sketchy borders
export const wobbleFactors = {
  subtle: 2,
  medium: 4,
  heavy: 8,
} as const;

// Paper line spacing
export const paperSpacing = {
  lined: 28, // pixels between lines
  grid: 24,  // grid square size
  dotted: 20, // dot spacing
} as const;

// Generate wobbly path for sketchy borders
export const generateWobblyRect = (
  width: number,
  height: number,
  wobble: 'subtle' | 'medium' | 'heavy' = 'subtle',
  seed: number = Math.random()
): string => {
  const factor = wobbleFactors[wobble];
  const seededRandom = (i: number) => {
    const x = Math.sin(seed * 9999 + i * 1234) * 10000;
    return x - Math.floor(x);
  };

  const w = width;
  const h = height;
  const v = factor;

  // Four corners with slight randomization
  const p0 = { x: v * seededRandom(0), y: v * seededRandom(1) };
  const p1 = { x: w + v * (seededRandom(2) - 0.5), y: v * seededRandom(3) };
  const p2 = { x: w + v * (seededRandom(4) - 0.5), y: h + v * (seededRandom(5) - 0.5) };
  const p3 = { x: v * (seededRandom(6) - 0.5), y: h + v * (seededRandom(7) - 0.5) };

  // Control points for curves
  const cp1 = { x: w / 2 + v * seededRandom(8), y: v * seededRandom(9) };
  const cp2 = { x: w + v * seededRandom(10), y: h / 2 + v * seededRandom(11) };
  const cp3 = { x: w / 2 + v * seededRandom(12), y: h + v * seededRandom(13) };
  const cp4 = { x: v * seededRandom(14), y: h / 2 + v * seededRandom(15) };

  return `
    M ${p0.x},${p0.y}
    Q ${cp1.x},${cp1.y} ${p1.x},${p1.y}
    Q ${cp2.x},${cp2.y} ${p2.x},${p2.y}
    Q ${cp3.x},${cp3.y} ${p3.x},${p3.y}
    Q ${cp4.x},${cp4.y} ${p0.x},${p0.y}
    Z
  `.trim();
};

// Generate wobbly underline path
export const generateWobblyUnderline = (
  width: number,
  wobble: 'subtle' | 'medium' | 'heavy' = 'subtle',
  seed: number = Math.random()
): string => {
  const factor = wobbleFactors[wobble];
  const seededRandom = (i: number) => {
    const x = Math.sin(seed * 9999 + i * 1234) * 10000;
    return x - Math.floor(x);
  };

  const segments = 5;
  const segmentWidth = width / segments;
  let path = `M 0,${factor * seededRandom(0)}`;

  for (let i = 1; i <= segments; i++) {
    const x = i * segmentWidth;
    const y = factor * (seededRandom(i) - 0.5);
    const cpX = (i - 0.5) * segmentWidth;
    const cpY = factor * (seededRandom(i + 10) - 0.5);
    path += ` Q ${cpX},${cpY} ${x},${y}`;
  }

  return path;
};

// Generate wobbly circle path
export const generateWobblyCircle = (
  radius: number,
  wobble: 'subtle' | 'medium' | 'heavy' = 'subtle',
  seed: number = Math.random()
): string => {
  const factor = wobbleFactors[wobble];
  const seededRandom = (i: number) => {
    const x = Math.sin(seed * 9999 + i * 1234) * 10000;
    return x - Math.floor(x);
  };

  const points = 8;
  const angleStep = (Math.PI * 2) / points;
  let path = '';

  for (let i = 0; i < points; i++) {
    const angle = i * angleStep;
    const nextAngle = ((i + 1) % points) * angleStep;
    const r = radius + factor * (seededRandom(i) - 0.5);
    const nextR = radius + factor * (seededRandom(i + points) - 0.5);

    const x = radius + r * Math.cos(angle);
    const y = radius + r * Math.sin(angle);
    const nextX = radius + nextR * Math.cos(nextAngle);
    const nextY = radius + nextR * Math.sin(nextAngle);

    const cpAngle = (angle + nextAngle) / 2;
    const cpR = radius + factor * (seededRandom(i + points * 2) - 0.5);
    const cpX = radius + cpR * Math.cos(cpAngle);
    const cpY = radius + cpR * Math.sin(cpAngle);

    if (i === 0) {
      path = `M ${x},${y}`;
    }
    path += ` Q ${cpX},${cpY} ${nextX},${nextY}`;
  }

  return path + ' Z';
};

export type NotebookPaletteType = typeof notebookPalette;
export type PencilColor = keyof typeof notebookPalette.pencil;
export type HighlighterColor = keyof typeof notebookPalette.highlighter;
export type ColoredPencilColor = keyof typeof notebookPalette.coloredPencil;
